import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  adminApi,
  type BillingPlanFull,
  type BillingPlanCreateData,
  type PlanLlmModel,
  type PlanLlmModelCreateData,
  type PlanLlmModelUpdateData,
  type DiscoveredModel,
} from '@/api/admin'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ModelProviderDot } from '@/components/ui/model-badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------
export default function AdminBillingPlans() {
  const queryClient = useQueryClient()
  const [selectedPlan, setSelectedPlan] = useState<BillingPlanFull | null>(null)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)

  const { data: plansRaw = [], isLoading } = useQuery({
    queryKey: ['admin-plans'],
    queryFn: adminApi.listPlans,
  })

  // Sort hidden plans below visible ones, then by price ascending. Lets
  // the admin scan active offerings first and tuck retired/internal plans
  // out of the way without losing access to them.
  const plans = [...plansRaw].sort((a, b) => {
    if (a.is_hidden !== b.is_hidden) return a.is_hidden ? 1 : -1
    return (a.price_cents ?? 0) - (b.price_cents ?? 0)
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.deletePlan(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-plans'] })
      setSelectedPlan(null)
    },
  })

  const duplicateMutation = useMutation({
    mutationFn: (id: string) => adminApi.duplicatePlan(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-plans'] })
    },
  })

  const [migrationBannerDismissed, setMigrationBannerDismissed] = useState(
    () => localStorage.getItem('billing_migration_015_banner_dismissed') === '1'
  )
  const dismissBanner = () => {
    localStorage.setItem('billing_migration_015_banner_dismissed', '1')
    setMigrationBannerDismissed(true)
  }

  return (
    <div className="p-6 space-y-6">
      {!migrationBannerDismissed && (
        <div className="rounded-md border border-yellow-300 bg-yellow-50 px-4 py-3 text-sm text-yellow-900 flex items-start justify-between gap-4">
          <div>
            <strong>Per-page pricing is live.</strong> Plans now charge per <em>page</em>, not per
            document. Per-field extraction mode has been removed — review the per-model
            <em> cr/page </em> and <em> cr/chat msg </em> rates for each plan and adjust before
            paying customers ramp.
          </div>
          <button
            type="button"
            onClick={dismissBanner}
            className="text-xs underline shrink-0"
          >
            Dismiss
          </button>
        </div>
      )}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Billing Plans</h1>
        <Button onClick={() => setCreateDialogOpen(true)}>Create Plan</Button>
      </div>

      {isLoading ? (
        <div className="text-center py-8 text-muted-foreground">Loading...</div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-3 font-medium">Plan</th>
                <th className="text-left p-3 font-medium">Price</th>
                <th className="text-left p-3 font-medium">Limits</th>
                <th className="text-left p-3 font-medium">Features</th>
                <th className="text-left p-3 font-medium">Models</th>
                <th className="text-left p-3 font-medium">Status</th>
                <th className="text-right p-3 font-medium w-20"></th>
              </tr>
            </thead>
            <tbody>
              {plans.map((plan) => (
                <tr
                  key={plan.id}
                  className={`border-t cursor-pointer transition-colors ${
                    selectedPlan?.id === plan.id ? 'bg-muted/40' : 'hover:bg-muted/20'
                  }`}
                  onClick={() => setSelectedPlan(plan)}
                >
                  <td className="p-3">
                    <p className="font-medium">{plan.name}</p>
                    <p className="text-xs text-muted-foreground">{plan.slug}</p>
                  </td>
                  <td className="p-3">
                    {plan.price_cents === 0
                      ? 'Free'
                      : `$${(plan.price_cents / 100).toFixed(0)}/${plan.billing_interval}`}
                  </td>
                  <td className="p-3 text-muted-foreground text-xs">
                    <p>
                      {plan.max_projects != null
                        ? `${plan.max_projects} projects`
                        : 'Unlimited projects'}
                    </p>
                    <p>
                      {plan.max_documents_per_project != null
                        ? `${plan.max_documents_per_project} docs/project`
                        : 'Unlimited docs'}
                    </p>
                  </td>
                  <td className="p-3">
                    <div className="flex gap-1 flex-wrap">
                      {plan.chat_enabled && (
                        <Badge variant="info" className="text-xs">
                          Chat
                        </Badge>
                      )}
                      {plan.whitelabel_enabled && (
                        <Badge variant="info" className="text-xs">
                          White Label
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td className="p-3">
                    <Badge variant="secondary" className="text-xs">
                      {plan.model_count ?? 0} models
                    </Badge>
                  </td>
                  <td className="p-3">
                    <div className="flex gap-1">
                      {!plan.is_active && (
                        <Badge variant="destructive" className="text-xs">
                          Inactive
                        </Badge>
                      )}
                      {plan.is_hidden && (
                        <Badge variant="secondary" className="text-xs">
                          Hidden
                        </Badge>
                      )}
                      {plan.is_active && !plan.is_hidden && (
                        <Badge variant="success" className="text-xs">
                          Active
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td className="p-3 text-right">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        if (confirm(`Duplicate "${plan.name}" with all settings and LLM models?`)) {
                          duplicateMutation.mutate(plan.id)
                        }
                      }}
                      className="text-xs text-primary hover:underline"
                      disabled={duplicateMutation.isPending}
                    >
                      Duplicate
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Plan Detail Panel */}
      {selectedPlan && (
        <PlanDetailPanel
          plan={selectedPlan}
          onClose={() => setSelectedPlan(null)}
          onDelete={() => {
            if (selectedPlan.slug === 'free') return
            if (confirm('Deactivate this plan? Active subscribers will not be affected.')) {
              deleteMutation.mutate(selectedPlan.id)
            }
          }}
        />
      )}

      {/* Create Plan Dialog */}
      <CreatePlanDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Plan Detail Panel (edit + model management)
// ---------------------------------------------------------------------------
function PlanDetailPanel({
  plan,
  onClose,
  onDelete,
}: {
  plan: BillingPlanFull
  onClose: () => void
  onDelete: () => void
}) {
  return (
    <div className="border rounded-lg bg-background shadow-sm">
      <div className="flex items-center justify-between p-4 border-b">
        <h2 className="text-lg font-semibold">
          {plan.name} <span className="text-sm text-muted-foreground">({plan.slug})</span>
        </h2>
        <div className="flex gap-2">
          {plan.slug !== 'free' && (
            <Button variant="destructive" size="sm" onClick={onDelete}>
              Deactivate
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>

      <Tabs defaultValue="settings" className="p-4">
        <TabsList>
          <TabsTrigger value="settings">Settings</TabsTrigger>
          <TabsTrigger value="models">LLM Models</TabsTrigger>
        </TabsList>

        <TabsContent value="settings">
          <PlanEditForm key={String(plan.id)} plan={plan} />
        </TabsContent>

        <TabsContent value="models">
          <PlanModelsManager planId={plan.id} />
        </TabsContent>
      </Tabs>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Plan Edit Form
// ---------------------------------------------------------------------------
function SyncStripeButton({ planId, hasStripeId, priceCents }: { planId: string; hasStripeId: boolean; priceCents: number }) {
  const queryClient = useQueryClient()
  const syncMutation = useMutation({
    mutationFn: () => adminApi.syncPlanStripe(planId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-plans'] }),
  })

  if (priceCents === 0) return <span className="text-xs text-muted-foreground self-center">Free plan — no Stripe sync needed</span>

  return (
    <>
      <Button
        variant={hasStripeId ? 'outline' : 'default'}
        size="sm"
        onClick={() => syncMutation.mutate()}
        disabled={syncMutation.isPending}
      >
        {syncMutation.isPending ? 'Syncing...' : hasStripeId ? 'Re-sync Stripe' : 'Sync to Stripe'}
      </Button>
      {hasStripeId && <Badge variant="outline" className="text-xs text-green-600 bg-green-50 self-center">Stripe linked</Badge>}
      {!hasStripeId && <Badge variant="outline" className="text-xs text-yellow-600 bg-yellow-50 self-center">Not linked to Stripe</Badge>}
      {syncMutation.isSuccess && <span className="text-xs text-green-600 self-center">Synced!</span>}
      {syncMutation.isError && <span className="text-xs text-red-600 self-center">Sync failed</span>}
    </>
  )
}

function PlanEditForm({ plan }: { plan: BillingPlanFull }) {
  const queryClient = useQueryClient()
  const [form, setForm] = useState({
    name: plan.name,
    description: plan.description || '',
    price_cents: plan.price_cents,
    billing_interval: plan.billing_interval,
    is_hidden: plan.is_hidden,
    is_active: plan.is_active,
    is_custom_pricing: plan.is_custom_pricing ?? false,
    max_projects: plan.max_projects,
    max_documents_per_project: plan.max_documents_per_project,
    max_fields_per_project: plan.max_fields_per_project,
    max_processed_documents: plan.max_processed_documents,
    max_users: plan.max_users,
    max_storage_mb: plan.max_storage_mb,
    credits_per_page_batch_mode: plan.credits_per_page_batch_mode,
    chat_credits_per_message: plan.chat_credits_per_message,
    credit_discount_factor: plan.credit_discount_factor,
    monthly_wallet_credits: plan.monthly_wallet_credits ?? 0,
    annual_price_cents: plan.annual_price_cents,
    annual_free_months: plan.annual_free_months,
    annual_discount_percent: plan.annual_discount_percent,
    chat_enabled: plan.chat_enabled,
    chat_retrieval_limit: plan.chat_retrieval_limit,
    chat_context_token_budget: plan.chat_context_token_budget,
    chat_min_similarity: plan.chat_min_similarity,
    whitelabel_enabled: plan.whitelabel_enabled ?? false,
  })

  const updateMutation = useMutation({
    mutationFn: (data: Partial<BillingPlanCreateData>) => adminApi.updatePlan(plan.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-plans'] })
    },
  })

  const handleSave = () => {
    updateMutation.mutate({
      name: form.name,
      description: form.description || null,
      price_cents: form.price_cents,
      billing_interval: form.billing_interval,
      is_hidden: form.is_hidden,
      is_active: form.is_active,
      is_custom_pricing: form.is_custom_pricing,
      max_projects: form.max_projects,
      max_documents_per_project: form.max_documents_per_project,
      max_fields_per_project: form.max_fields_per_project,
      max_processed_documents: form.max_processed_documents,
      max_users: form.max_users,
      max_storage_mb: form.max_storage_mb,
      credits_per_page_batch_mode: form.credits_per_page_batch_mode,
      chat_credits_per_message: form.chat_credits_per_message,
      credit_discount_factor: form.credit_discount_factor,
      monthly_wallet_credits: form.monthly_wallet_credits,
      annual_price_cents: form.annual_price_cents,
      annual_free_months: form.annual_free_months,
      annual_discount_percent: form.annual_discount_percent,
      chat_enabled: form.chat_enabled,
      chat_retrieval_limit: form.chat_retrieval_limit,
      chat_context_token_budget: form.chat_context_token_budget,
      chat_min_similarity: form.chat_min_similarity,
      whitelabel_enabled: form.whitelabel_enabled,
    })
  }

  const setField = (key: string, value: unknown) => setForm((prev) => ({ ...prev, [key]: value }))
  const numOrNull = (v: string) => (v === '' ? null : Number(v))

  return (
    <div className="space-y-6 max-w-2xl">
      {/* General */}
      <section className="space-y-3">
        <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
          General
        </h3>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Name</Label>
            <Input value={form.name} onChange={(e) => setField('name', e.target.value)} />
          </div>
          <div>
            <Label>Price (cents)</Label>
            <Input
              type="number"
              value={form.price_cents}
              onChange={(e) => setField('price_cents', Number(e.target.value))}
            />
          </div>
          <div>
            <Label>Billing Interval</Label>
            <Select
              value={form.billing_interval}
              onValueChange={(v) => setField('billing_interval', v)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="month">Month</SelectItem>
                <SelectItem value="year">Year</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Description</Label>
            <Input
              value={form.description}
              onChange={(e) => setField('description', e.target.value)}
            />
          </div>
        </div>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.is_hidden}
              onChange={(e) => setField('is_hidden', e.target.checked)}
              className="rounded"
            />
            Hidden
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setField('is_active', e.target.checked)}
              className="rounded"
            />
            Active
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.whitelabel_enabled}
              onChange={(e) => setField('whitelabel_enabled', e.target.checked)}
              className="rounded"
            />
            Enable white-label branding
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.is_custom_pricing}
              onChange={(e) => setField('is_custom_pricing', e.target.checked)}
              className="rounded"
            />
            Custom Pricing (enterprise/manual billing)
          </label>
        </div>
      </section>

      {/* Annual Pricing */}
      <section className="space-y-3">
        <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
          Annual Pricing
        </h3>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label>Annual Price (cents)</Label>
            <Input
              type="number"
              value={form.annual_price_cents ?? ''}
              placeholder="Not offered"
              onChange={(e) => setField('annual_price_cents', numOrNull(e.target.value))}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Leave blank if annual billing is not offered
            </p>
          </div>
          <div>
            <Label>Free Months (annual)</Label>
            <Input
              type="number"
              min="0"
              max="11"
              value={form.annual_free_months}
              onChange={(e) => setField('annual_free_months', Number(e.target.value))}
            />
            <p className="text-xs text-muted-foreground mt-1">
              e.g. 2 = "pay for 10, get 12"
            </p>
          </div>
          <div>
            <Label>Discount % (annual)</Label>
            <Input
              type="number"
              min="0"
              max="100"
              value={form.annual_discount_percent}
              onChange={(e) => setField('annual_discount_percent', Number(e.target.value))}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Alternative to free months
            </p>
          </div>
        </div>
      </section>

      {/* Limits */}
      <section className="space-y-3">
        <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
          Limits (blank = unlimited)
        </h3>
        <div className="grid grid-cols-3 gap-3">
          {(
            [
              ['max_projects', 'Projects'],
              ['max_documents_per_project', 'Docs / Project'],
              ['max_fields_per_project', 'Fields / Project'],
              ['max_processed_documents', 'Processed Docs'],
              ['max_users', 'Users'],
              ['max_storage_mb', 'Storage (MB)'],
            ] as const
          ).map(([key, label]) => (
            <div key={key}>
              <Label>{label}</Label>
              <Input
                type="number"
                value={form[key] ?? ''}
                placeholder="Unlimited"
                onChange={(e) => setField(key, numOrNull(e.target.value))}
              />
            </div>
          ))}
        </div>
      </section>

      {/* Credits */}
      <section className="space-y-3">
        <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
          Credits (fallback rates)
        </h3>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Credits per page</Label>
            <Input
              type="number"
              step="0.01"
              value={form.credits_per_page_batch_mode}
              onChange={(e) => setField('credits_per_page_batch_mode', Number(e.target.value))}
            />
          </div>
          <div>
            <Label>Credits per chat message</Label>
            <Input
              type="number"
              step="0.01"
              value={form.chat_credits_per_message}
              onChange={(e) => setField('chat_credits_per_message', Number(e.target.value))}
            />
          </div>
          <div>
            <Label>Discount Factor</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              max="2"
              value={form.credit_discount_factor}
              onChange={(e) => setField('credit_discount_factor', Number(e.target.value))}
            />
          </div>
          <div className="space-y-1">
            <Label>Monthly Wallet Credits</Label>
            <Input
              type="number"
              min="0"
              value={form.monthly_wallet_credits ?? 0}
              onChange={(e) => setField('monthly_wallet_credits', Number(e.target.value))}
            />
            <p className="text-xs text-muted-foreground">
              Auto-credited to wallet each billing cycle (e.g. 5000 = $50.00/mo)
            </p>
          </div>
        </div>
      </section>

      {/* Chat */}
      <section className="space-y-3">
        <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
          Chat Settings
        </h3>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.chat_enabled}
            onChange={(e) => setField('chat_enabled', e.target.checked)}
            className="rounded"
          />
          Chat Enabled
        </label>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label>Retrieval Limit</Label>
            <Input
              type="number"
              value={form.chat_retrieval_limit}
              onChange={(e) => setField('chat_retrieval_limit', Number(e.target.value))}
            />
          </div>
          <div>
            <Label>Context Token Budget</Label>
            <Input
              type="number"
              value={form.chat_context_token_budget}
              onChange={(e) => setField('chat_context_token_budget', Number(e.target.value))}
            />
          </div>
          <div>
            <Label>Min Similarity</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              max="1"
              value={form.chat_min_similarity}
              onChange={(e) => setField('chat_min_similarity', Number(e.target.value))}
            />
          </div>
        </div>
      </section>

      <div className="flex gap-2 items-center">
        <Button onClick={handleSave} disabled={updateMutation.isPending}>
          {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
        </Button>
        <SyncStripeButton planId={String(plan.id)} hasStripeId={!!plan.stripe_product_id} priceCents={plan.price_cents} />
        {updateMutation.isSuccess && (
          <span className="text-sm text-green-600 self-center">Saved</span>
        )}
        {updateMutation.isError && (
          <span className="text-sm text-red-600 self-center">
            Error: {(updateMutation.error as Error).message}
          </span>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Plan Models Manager (table + add/edit/remove)
// ---------------------------------------------------------------------------
function PlanModelsManager({ planId }: { planId: string }) {
  const queryClient = useQueryClient()
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [editingModel, setEditingModel] = useState<PlanLlmModel | null>(null)

  const { data: models = [], isLoading } = useQuery({
    queryKey: ['admin-plan-models', planId],
    queryFn: () => adminApi.listPlanModels(planId),
  })

  const removeMutation = useMutation({
    mutationFn: (modelId: string) => adminApi.removePlanModel(planId, modelId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-plan-models', planId] })
      queryClient.invalidateQueries({ queryKey: ['admin-plans'] })
    },
  })

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Models assigned to this plan. Credit rates are per-model.
        </p>
        <Button size="sm" onClick={() => setAddDialogOpen(true)}>
          Add Model
        </Button>
      </div>

      {isLoading ? (
        <div className="text-sm text-muted-foreground py-4 text-center">Loading...</div>
      ) : models.length === 0 ? (
        <div className="text-sm text-muted-foreground py-8 text-center border rounded-lg">
          No models assigned. Users on this plan cannot run extractions or chat.
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-2 font-medium">Model</th>
                <th className="text-left p-2 font-medium">Display Name</th>
                <th className="text-center p-2 font-medium">Default</th>
                <th className="text-right p-2 font-medium">cr / page</th>
                <th className="text-right p-2 font-medium">cr / chat msg</th>
                <th className="text-center p-2 font-medium">Order</th>
                <th className="text-center p-2 font-medium">Active</th>
                <th className="text-right p-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {models.map((model) => (
                <tr key={model.id} className="border-t hover:bg-muted/20">
                  <td className="p-2 font-mono text-xs">
                    <span className="inline-flex items-center gap-1.5">
                      <ModelProviderDot modelName={model.model_name} />
                      {model.model_name}
                    </span>
                  </td>
                  <td className="p-2">{model.display_name}</td>
                  <td className="p-2 text-center">
                    {model.is_default && (
                      <Badge variant="info" className="text-xs">
                        Default
                      </Badge>
                    )}
                  </td>
                  <td className="p-2 text-right">{Number(model.credits_per_page_batch_mode)}</td>
                  <td className="p-2 text-right">{Number(model.chat_credits_per_message)}</td>
                  <td className="p-2 text-center">{model.sort_order}</td>
                  <td className="p-2 text-center">
                    {model.is_active ? (
                      <Badge variant="success" className="text-xs">
                        Yes
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="text-xs">
                        No
                      </Badge>
                    )}
                  </td>
                  <td className="p-2 text-right">
                    <div className="flex gap-1 justify-end">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingModel(model)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => {
                          if (confirm(`Remove ${model.display_name} from this plan?`)) {
                            removeMutation.mutate(model.id)
                          }
                        }}
                      >
                        Remove
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Model Dialog */}
      <AddModelDialog
        planId={planId}
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        existingModelNames={models.map((m) => m.model_name)}
      />

      {/* Edit Model Dialog */}
      {editingModel && (
        <EditModelDialog
          planId={planId}
          model={editingModel}
          open={true}
          onOpenChange={(open) => {
            if (!open) setEditingModel(null)
          }}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Searchable Model Dropdown
// ---------------------------------------------------------------------------
function SearchableModelSelect({
  models,
  grouped,
  selectedModelId,
  searchTerm,
  onSearchChange,
  onSelect,
}: {
  models: DiscoveredModel[]
  grouped: Record<string, DiscoveredModel[]>
  selectedModelId: string
  searchTerm: string
  onSearchChange: (s: string) => void
  onSelect: (modelId: string) => void
}) {
  const [open, setOpen] = useState(false)
  const ref = React.useRef<HTMLDivElement>(null)
  const selected = models.find((m) => m.model_id === selectedModelId) ||
    (selectedModelId ? { model_id: selectedModelId, display_name: selectedModelId, provider: '' } : null)

  React.useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [])

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => { setOpen(!open); onSearchChange('') }}
        className="w-full flex items-center justify-between border rounded-md px-3 py-2 text-sm bg-background hover:bg-muted/50"
      >
        {selected ? (
          <span className="flex items-center gap-2">
            <ModelProviderDot modelName={selected.model_id} />
            {selected.display_name}
            <span className="text-xs text-muted-foreground">({selected.model_id})</span>
          </span>
        ) : (
          <span className="text-muted-foreground">Select a model...</span>
        )}
        <svg className="h-4 w-4 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute z-50 top-full mt-1 w-full bg-background border rounded-lg shadow-lg">
          <div className="p-2 border-b">
            <Input
              placeholder="Search models..."
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              autoFocus
              className="h-8 text-sm"
            />
          </div>
          <div className="max-h-64 overflow-y-auto p-1">
            {Object.entries(grouped).length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-3">No matching models</p>
            ) : (
              Object.entries(grouped).map(([provider, providerModels]) => (
                <div key={provider}>
                  <div className="px-3 py-1.5 text-[10px] font-semibold uppercase text-muted-foreground tracking-wider">
                    {provider}
                  </div>
                  {providerModels.map((m) => (
                    <button
                      key={m.model_id}
                      type="button"
                      onClick={() => { onSelect(m.model_id); setOpen(false) }}
                      className={`w-full text-left px-3 py-1.5 text-sm rounded hover:bg-muted flex items-center gap-2 ${
                        selectedModelId === m.model_id ? 'bg-muted font-medium' : ''
                      }`}
                    >
                      <ModelProviderDot modelName={m.model_id} />
                      <span>{m.display_name}</span>
                      <span className="text-xs text-muted-foreground ml-auto">({m.model_id})</span>
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Add Model Dialog (with discovery)
// ---------------------------------------------------------------------------
function AddModelDialog({
  planId,
  open,
  onOpenChange,
  existingModelNames,
}: {
  planId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  existingModelNames: string[]
}) {
  const queryClient = useQueryClient()
  const [form, setForm] = useState<PlanLlmModelCreateData>({
    model_name: '',
    display_name: '',
    is_default: false,
    credits_per_page_batch_mode: 1.0,
    chat_credits_per_message: 0.5,
    sort_order: 0,
    is_active: true,
  })
  const [searchTerm, setSearchTerm] = useState('')

  const {
    data: discoveredModels = [],
    isLoading: discovering,
    refetch: refetchModels,
  } = useQuery({
    queryKey: ['admin-discover-models'],
    queryFn: adminApi.discoverModels,
    enabled: open,
    staleTime: 60 * 60 * 1000, // 1 hour
  })

  const addMutation = useMutation({
    mutationFn: (data: PlanLlmModelCreateData) => adminApi.addPlanModel(planId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-plan-models', planId] })
      queryClient.invalidateQueries({ queryKey: ['admin-plans'] })
      onOpenChange(false)
      setForm({
        model_name: '',
        display_name: '',
        is_default: false,
        credits_per_page_batch_mode: 1.0,
            chat_credits_per_message: 0.5,
        sort_order: 0,
        is_active: true,
      })
      setSearchTerm('')
    },
  })

  // Filter out already-assigned models and apply search
  const availableModels = discoveredModels.filter(
    (m) =>
      !existingModelNames.includes(m.model_id) &&
      (searchTerm === '' ||
        m.model_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        m.display_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        m.provider.toLowerCase().includes(searchTerm.toLowerCase()))
  )

  // Group by provider
  const grouped = availableModels.reduce<Record<string, DiscoveredModel[]>>((acc, m) => {
    if (!acc[m.provider]) acc[m.provider] = []
    acc[m.provider].push(m)
    return acc
  }, {})

  const handleSelectModel = (modelId: string) => {
    const found = discoveredModels.find((m) => m.model_id === modelId)
    if (found) {
      setForm((prev) => ({
        ...prev,
        model_name: found.model_id,
        display_name: found.display_name,
      }))
    }
  }

  const setField = (key: string, value: unknown) => setForm((prev) => ({ ...prev, [key]: value }))

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add LLM Model to Plan</DialogTitle>
          <DialogDescription>
            Select a model from your configured providers and set credit rates.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Model selector */}
          <div className="space-y-2">
            <Label>Model</Label>
            {discovering ? (
              <div className="text-sm text-muted-foreground py-2">
                Discovering models from providers...
              </div>
            ) : discoveredModels.length === 0 ? (
              <div className="text-sm text-muted-foreground py-2">
                No models discovered. Check that OPENAI_API_KEY and/or ANTHROPIC_API_KEY are
                configured.
                <Button variant="outline" size="sm" className="ml-2" onClick={() => refetchModels()}>
                  Retry
                </Button>
              </div>
            ) : (
              <SearchableModelSelect
                models={availableModels}
                grouped={grouped}
                selectedModelId={form.model_name}
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                onSelect={handleSelectModel}
              />
            )}
            {/* Manual entry fallback */}
            <div className="pt-1">
              <Label className="text-xs text-muted-foreground">
                Or enter a model ID manually:
              </Label>
              <Input
                value={form.model_name}
                onChange={(e) => {
                  setField('model_name', e.target.value)
                  if (!form.display_name || form.display_name === form.model_name) {
                    setField('display_name', e.target.value)
                  }
                }}
                placeholder="e.g. gpt-4o-mini"
              />
            </div>
          </div>

          {/* Display name */}
          <div>
            <Label>Display Name</Label>
            <Input
              value={form.display_name}
              onChange={(e) => setField('display_name', e.target.value)}
              placeholder="e.g. GPT-4o Mini"
            />
          </div>

          {/* Credit rates */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Credits per page</Label>
              <Input
                type="number"
                step="0.01"
                value={form.credits_per_page_batch_mode}
                onChange={(e) => setField('credits_per_page_batch_mode', Number(e.target.value))}
              />
            </div>
            <div>
              <Label>Credits per chat message</Label>
              <Input
                type="number"
                step="0.01"
                value={form.chat_credits_per_message}
                onChange={(e) => setField('chat_credits_per_message', Number(e.target.value))}
              />
            </div>
          </div>

          {/* Options */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Sort Order</Label>
              <Input
                type="number"
                value={form.sort_order}
                onChange={(e) => setField('sort_order', Number(e.target.value))}
              />
            </div>
            <div className="flex flex-col justify-end gap-2">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.is_default}
                  onChange={(e) => setField('is_default', e.target.checked)}
                  className="rounded"
                />
                Default model
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setField('is_active', e.target.checked)}
                  className="rounded"
                />
                Active
              </label>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!form.model_name || !form.display_name || addMutation.isPending}
            onClick={() => addMutation.mutate(form)}
          >
            {addMutation.isPending ? 'Adding...' : 'Add Model'}
          </Button>
        </DialogFooter>

        {addMutation.isError && (
          <p className="text-sm text-red-600">
            {(addMutation.error as { response?: { data?: { detail?: string } } })?.response?.data
              ?.detail || (addMutation.error as Error).message}
          </p>
        )}
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Edit Model Dialog
// ---------------------------------------------------------------------------
function EditModelDialog({
  planId,
  model,
  open,
  onOpenChange,
}: {
  planId: string
  model: PlanLlmModel
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const queryClient = useQueryClient()
  const [form, setForm] = useState<PlanLlmModelUpdateData>({
    display_name: model.display_name,
    is_default: model.is_default,
    credits_per_page_batch_mode: Number(model.credits_per_page_batch_mode),
    chat_credits_per_message: Number(model.chat_credits_per_message),
    sort_order: model.sort_order,
    is_active: model.is_active,
  })

  const updateMutation = useMutation({
    mutationFn: (data: PlanLlmModelUpdateData) =>
      adminApi.updatePlanModel(planId, model.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-plan-models', planId] })
      queryClient.invalidateQueries({ queryKey: ['admin-plans'] })
      onOpenChange(false)
    },
  })

  const setField = (key: string, value: unknown) => setForm((prev) => ({ ...prev, [key]: value }))

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Model</DialogTitle>
          <DialogDescription>
            <span className="font-mono text-xs">{model.model_name}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Display Name</Label>
            <Input
              value={form.display_name}
              onChange={(e) => setField('display_name', e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Credits per page</Label>
              <Input
                type="number"
                step="0.01"
                value={form.credits_per_page_batch_mode}
                onChange={(e) => setField('credits_per_page_batch_mode', Number(e.target.value))}
              />
            </div>
            <div>
              <Label>Credits per chat message</Label>
              <Input
                type="number"
                step="0.01"
                value={form.chat_credits_per_message}
                onChange={(e) => setField('chat_credits_per_message', Number(e.target.value))}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Sort Order</Label>
              <Input
                type="number"
                value={form.sort_order}
                onChange={(e) => setField('sort_order', Number(e.target.value))}
              />
            </div>
            <div className="flex flex-col justify-end gap-2">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.is_default}
                  onChange={(e) => setField('is_default', e.target.checked)}
                  className="rounded"
                />
                Default model
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setField('is_active', e.target.checked)}
                  className="rounded"
                />
                Active
              </label>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={updateMutation.isPending}
            onClick={() => updateMutation.mutate(form)}
          >
            {updateMutation.isPending ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>

        {updateMutation.isError && (
          <p className="text-sm text-red-600">
            {(updateMutation.error as Error).message}
          </p>
        )}
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Create Plan Dialog
// ---------------------------------------------------------------------------
function CreatePlanDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const queryClient = useQueryClient()
  const defaultForm: BillingPlanCreateData = {
    name: '',
    slug: '',
    price_cents: 0,
    billing_interval: 'month',
    description: '',
    is_hidden: false,
    is_active: true,
    is_custom_pricing: false,
    max_projects: null,
    max_documents_per_project: null,
    max_fields_per_project: null,
    max_processed_documents: null,
    max_users: null,
    max_storage_mb: null,
    credits_per_page_batch_mode: 1.0,
    chat_credits_per_message: 0.5,
    credit_discount_factor: 1.0,
    annual_price_cents: null,
    annual_free_months: 0,
    annual_discount_percent: 0,
    chat_enabled: false,
    chat_retrieval_limit: 20,
    chat_context_token_budget: 4000,
    chat_min_similarity: 0.7,
    whitelabel_enabled: false,
  }
  const [form, setForm] = useState<BillingPlanCreateData>({ ...defaultForm })

  const createMutation = useMutation({
    mutationFn: (data: BillingPlanCreateData) => adminApi.createPlan(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-plans'] })
      onOpenChange(false)
      setForm({ ...defaultForm })
    },
  })

  const setField = (key: string, value: unknown) => setForm((prev) => ({ ...prev, [key]: value }))
  const numOrNull = (v: string) => (v === '' ? null : Number(v))

  // Auto-generate slug from name
  const handleNameChange = (name: string) => {
    setField('name', name)
    // Only auto-slug if slug hasn't been manually edited
    const autoSlug = name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
    setField('slug', autoSlug)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Billing Plan</DialogTitle>
          <DialogDescription>
            Define a new billing plan with limits and credit rates.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* General */}
          <section className="space-y-3">
            <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
              General
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Name *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  placeholder="e.g. Pro"
                />
              </div>
              <div>
                <Label>Slug *</Label>
                <Input
                  value={form.slug}
                  onChange={(e) => setField('slug', e.target.value)}
                  placeholder="e.g. pro"
                />
              </div>
              <div>
                <Label>Price (cents)</Label>
                <Input
                  type="number"
                  value={form.price_cents}
                  onChange={(e) => setField('price_cents', Number(e.target.value))}
                  placeholder="4900 = $49"
                />
              </div>
              <div>
                <Label>Billing Interval</Label>
                <Select
                  value={form.billing_interval}
                  onValueChange={(v) => setField('billing_interval', v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="month">Month</SelectItem>
                    <SelectItem value="year">Year</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Input
                value={form.description || ''}
                onChange={(e) => setField('description', e.target.value)}
                placeholder="Plan description"
              />
            </div>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.is_hidden}
                  onChange={(e) => setField('is_hidden', e.target.checked)}
                  className="rounded"
                />
                Hidden
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setField('is_active', e.target.checked)}
                  className="rounded"
                />
                Active
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.whitelabel_enabled}
                  onChange={(e) => setField('whitelabel_enabled', e.target.checked)}
                  className="rounded"
                />
                Enable white-label branding
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.is_custom_pricing}
                  onChange={(e) => setField('is_custom_pricing', e.target.checked)}
                  className="rounded"
                />
                Custom Pricing (enterprise)
              </label>
            </div>
          </section>

          {/* Annual Pricing */}
          <section className="space-y-3">
            <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
              Annual Pricing
            </h3>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Annual Price (cents)</Label>
                <Input
                  type="number"
                  value={form.annual_price_cents ?? ''}
                  placeholder="Not offered"
                  onChange={(e) => setField('annual_price_cents', numOrNull(e.target.value))}
                />
              </div>
              <div>
                <Label>Free Months (annual)</Label>
                <Input
                  type="number"
                  min="0"
                  max="11"
                  value={form.annual_free_months ?? 0}
                  onChange={(e) => setField('annual_free_months', Number(e.target.value))}
                />
              </div>
              <div>
                <Label>Discount % (annual)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={form.annual_discount_percent ?? 0}
                  onChange={(e) => setField('annual_discount_percent', Number(e.target.value))}
                />
              </div>
            </div>
          </section>

          {/* Limits */}
          <section className="space-y-3">
            <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
              Limits (blank = unlimited)
            </h3>
            <div className="grid grid-cols-3 gap-3">
              {(
                [
                  ['max_projects', 'Projects'],
                  ['max_documents_per_project', 'Docs / Project'],
                  ['max_fields_per_project', 'Fields / Project'],
                  ['max_processed_documents', 'Processed Docs'],
                  ['max_users', 'Users'],
                  ['max_storage_mb', 'Storage (MB)'],
                ] as const
              ).map(([key, label]) => (
                <div key={key}>
                  <Label>{label}</Label>
                  <Input
                    type="number"
                    value={form[key] ?? ''}
                    placeholder="Unlimited"
                    onChange={(e) => setField(key, numOrNull(e.target.value))}
                  />
                </div>
              ))}
            </div>
          </section>

          {/* Credits */}
          <section className="space-y-3">
            <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
              Credits (fallback rates)
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Credits per page</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.credits_per_page_batch_mode}
                  onChange={(e) => setField('credits_per_page_batch_mode', Number(e.target.value))}
                />
              </div>
              <div>
                <Label>Credits per chat message</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.chat_credits_per_message}
                  onChange={(e) => setField('chat_credits_per_message', Number(e.target.value))}
                />
              </div>
              <div>
                <Label>Discount Factor</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="2"
                  value={form.credit_discount_factor}
                  onChange={(e) => setField('credit_discount_factor', Number(e.target.value))}
                />
              </div>
            </div>
          </section>

          {/* Chat */}
          <section className="space-y-3">
            <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
              Chat Settings
            </h3>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.chat_enabled}
                onChange={(e) => setField('chat_enabled', e.target.checked)}
                className="rounded"
              />
              Chat Enabled
            </label>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Retrieval Limit</Label>
                <Input
                  type="number"
                  value={form.chat_retrieval_limit}
                  onChange={(e) => setField('chat_retrieval_limit', Number(e.target.value))}
                />
              </div>
              <div>
                <Label>Context Token Budget</Label>
                <Input
                  type="number"
                  value={form.chat_context_token_budget}
                  onChange={(e) => setField('chat_context_token_budget', Number(e.target.value))}
                />
              </div>
              <div>
                <Label>Min Similarity</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="1"
                  value={form.chat_min_similarity}
                  onChange={(e) => setField('chat_min_similarity', Number(e.target.value))}
                />
              </div>
            </div>
          </section>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!form.name || !form.slug || createMutation.isPending}
            onClick={() => createMutation.mutate(form)}
          >
            {createMutation.isPending ? 'Creating...' : 'Create Plan'}
          </Button>
        </DialogFooter>

        {createMutation.isError && (
          <p className="text-sm text-red-600">
            {(createMutation.error as { response?: { data?: { detail?: string } } })?.response?.data
              ?.detail || (createMutation.error as Error).message}
          </p>
        )}
      </DialogContent>
    </Dialog>
  )
}
